import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getCommunications, createCommunication, updateCommunication,
  archiveCommunication, getCompanies, getViolations, getCommunicationPeople,
} from "../../api/client";
import { CommunicationLog, Violation } from "../../types";

/**
 * Control Authority ↔ Industrial User communication log — documents the informal
 * contacts (phone calls, voicemails, emails, in-person, letters) that support
 * pretreatment recordkeeping (40 CFR 403.12(o)) and the enforcement ladder.
 *
 * An entry can flag a follow-up (a callback / action owed); "Mark resolved"
 * clears it. Entries are soft-archived, never hard-deleted.
 */
const METHODS: { v: string; label: string }[] = [
  { v: "phone", label: "Phone call" },
  { v: "voicemail", label: "Voicemail" },
  { v: "email", label: "Email" },
  { v: "in_person", label: "In person" },
  { v: "letter", label: "Letter" },
  { v: "other", label: "Other" },
];
const methodLabel = (v: string) => METHODS.find(m => m.v === v)?.label ?? v;

const today = () => new Date().toISOString().slice(0, 10);

const blankForm = () => ({
  company_id: "", contact_date: today(), direction: "outbound", method: "phone",
  ca_staff_user_id: "", iu_contact_user_id: "", subject: "", summary: "", follow_up_required: false,
  related_violation_id: "",
});

// A person who may be named on a contact (a user account in the program).
interface Person { id: number; name: string; title?: string | null; username: string }
// "Pat Lee, Plant Manager (plee)"; the title and the real name are each optional.
const withTitle = (name: string, title?: string | null) => (title ? `${name}, ${title}` : name);
const personLabel = (p: Person) =>
  p.name === p.username ? withTitle(p.username, p.title) : `${withTitle(p.name, p.title)} (${p.username})`;

const violationLabel = (v: Violation) =>
  `${v.parameter_name ?? "Violation"} · ${v.violation_type.replace("_", " ")} · ${v.violation_date}`;

const CommunicationsPanel: React.FC = () => {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [filterCompany, setFilterCompany] = useState<string>("");
  const [companies, setCompanies] = useState<{ id: number; name: string }[]>([]);
  const [log, setLog] = useState<CommunicationLog[]>([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [formViolations, setFormViolations] = useState<Violation[]>([]);
  const [caStaff, setCaStaff] = useState<Person[]>([]);
  const [iuContacts, setIuContacts] = useState<Person[]>([]);

  const load = useCallback(() => {
    getCommunications({
      year,
      company_id: filterCompany ? Number(filterCompany) : undefined,
    }).then(r => setLog(r.data)).catch(() => {});
  }, [year, filterCompany]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getCompanies().then(r => setCompanies(r.data.map((c: any) => ({ id: c.id, name: c.name }))))
      .catch(() => {});
  }, []);

  // Load the selected IU's violations so a contact can be linked to the one it was about.
  useEffect(() => {
    if (!form.company_id) { setFormViolations([]); return; }
    getViolations(Number(form.company_id)).then(r => setFormViolations(r.data)).catch(() => setFormViolations([]));
  }, [form.company_id]);

  // People are picked from user accounts, never typed: CA staff from the
  // Control Authority's users, the IU contact from the selected company's users.
  // The CA side defaults to whoever is logging the contact.
  useEffect(() => {
    const companyId = form.company_id ? Number(form.company_id) : undefined;
    getCommunicationPeople(companyId).then(r => {
      setCaStaff(r.data.ca_staff ?? []);
      setIuContacts(r.data.iu_contacts ?? []);
      setForm(f => (f.ca_staff_user_id === "" && r.data.me ? { ...f, ca_staff_user_id: String(r.data.me) } : f));
    }).catch(() => { setCaStaff([]); setIuContacts([]); });
  }, [form.company_id]);

  const openFollowups = useMemo(() => log.filter(c => c.follow_up_open), [log]);

  const submit = async () => {
    setError("");
    if (!form.company_id) { setError("Choose an industrial user."); return; }
    if (!form.contact_date) { setError("Contact date is required."); return; }
    if (!form.ca_staff_user_id) { setError("Choose the CA staff member."); return; }
    setSaving(true);
    try {
      await createCommunication({
        company_id: Number(form.company_id),
        contact_date: form.contact_date,
        direction: form.direction,
        method: form.method,
        ca_staff_user_id: Number(form.ca_staff_user_id),
        iu_contact_user_id: form.iu_contact_user_id ? Number(form.iu_contact_user_id) : undefined,
        subject: form.subject || undefined,
        summary: form.summary || undefined,
        follow_up_required: form.follow_up_required,
        related_violation_id: form.related_violation_id ? Number(form.related_violation_id) : undefined,
      });
      setShowForm(false);
      setForm(f => ({ ...blankForm(), company_id: f.company_id, ca_staff_user_id: f.ca_staff_user_id }));
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Could not save the communication.");
    } finally {
      setSaving(false);
    }
  };

  const resolveFollowup = async (c: CommunicationLog) => {
    try { await updateCommunication(c.id, { resolve_followup: true }); load(); }
    catch (e: any) { setError(e?.response?.data?.error ?? "Could not resolve follow-up."); }
  };

  const onArchive = async (c: CommunicationLog) => {
    if (!window.confirm(`Archive the ${c.contact_date} contact with ${c.company_name}? It stays on record but is hidden.`)) return;
    try { await archiveCommunication(c.id); load(); }
    catch (e: any) { setError(e?.response?.data?.error ?? "Could not archive."); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18 }}>Communication log</h3>
          <p style={{ margin: "2px 0 0", color: "#718096", fontSize: 13 }}>
            Record of contacts between the Control Authority and its industrial users — calls, emails, and conversations.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} style={sel}>
            <option value="">All IUs</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={sel}>
            {[0, 1, 2].map(d => new Date().getFullYear() - d).map(y =>
              <option key={y} value={y}>{y}</option>)}
          </select>
          <button style={btn} onClick={() => setShowForm(v => !v)}>
            {showForm ? "Close" : "+ Log contact"}
          </button>
        </div>
      </div>

      {error && <p style={{ color: "#c53030", fontSize: 13 }}>{error}</p>}

      {/* Add form */}
      {showForm && (
        <div style={card}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={field}><span style={lbl}>Industrial user</span>
              <select style={inp} value={form.company_id}
                onChange={e => setForm(f => ({ ...f, company_id: e.target.value, related_violation_id: "", iu_contact_user_id: "" }))}>
                <option value="">— select —</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label style={field}><span style={lbl}>Date</span>
              <input type="date" style={inp} value={form.contact_date}
                onChange={e => setForm(f => ({ ...f, contact_date: e.target.value }))} />
            </label>
            <label style={field}><span style={lbl}>Direction</span>
              <select style={inp} value={form.direction}
                onChange={e => setForm(f => ({ ...f, direction: e.target.value }))}>
                <option value="outbound">Outbound (CA → IU)</option>
                <option value="inbound">Inbound (IU → CA)</option>
              </select>
            </label>
            <label style={field}><span style={lbl}>Method</span>
              <select style={inp} value={form.method}
                onChange={e => setForm(f => ({ ...f, method: e.target.value }))}>
                {METHODS.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
              </select>
            </label>
            <label style={field}><span style={lbl}>CA staff</span>
              <select style={inp} value={form.ca_staff_user_id}
                onChange={e => setForm(f => ({ ...f, ca_staff_user_id: e.target.value }))}>
                <option value="">— select —</option>
                {caStaff.map(p => <option key={p.id} value={p.id}>{personLabel(p)}</option>)}
              </select>
            </label>
            <label style={field}><span style={lbl}>IU contact</span>
              <select style={inp} value={form.iu_contact_user_id} disabled={!form.company_id}
                onChange={e => setForm(f => ({ ...f, iu_contact_user_id: e.target.value }))}>
                <option value="">{!form.company_id ? "— choose an industrial user first —" : "— none —"}</option>
                {iuContacts.map(p => <option key={p.id} value={p.id}>{personLabel(p)}</option>)}
              </select>
              {form.company_id && iuContacts.length === 0 && (
                <span style={{ fontSize: 12, color: "#718096" }}>
                  This industrial user has no user accounts yet. An admin can add one under Users.
                </span>
              )}
            </label>
            <label style={{ ...field, gridColumn: "1 / -1" }}><span style={lbl}>Subject</span>
              <input style={inp} value={form.subject} placeholder="Short subject line"
                onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} />
            </label>
            <label style={{ ...field, gridColumn: "1 / -1" }}><span style={lbl}>Summary</span>
              <textarea style={{ ...inp, minHeight: 64 }} value={form.summary}
                placeholder="What was discussed"
                onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} />
            </label>
            <label style={{ ...field, gridColumn: "1 / -1" }}>
              <span style={lbl}>Related violation <span style={{ fontWeight: 400, color: "#718096" }}>(optional)</span></span>
              <select style={inp} value={form.related_violation_id}
                disabled={!form.company_id || formViolations.length === 0}
                onChange={e => setForm(f => ({ ...f, related_violation_id: e.target.value }))}>
                <option value="">
                  {!form.company_id ? "— choose an IU first —"
                    : formViolations.length === 0 ? "— no violations on record —"
                    : "— none —"}
                </option>
                {formViolations.map(v => <option key={v.id} value={v.id}>{violationLabel(v)}</option>)}
              </select>
            </label>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 13, color: "#4a5568" }}>
            <input type="checkbox" checked={form.follow_up_required}
              onChange={e => setForm(f => ({ ...f, follow_up_required: e.target.checked }))} />
            Follow-up needed (a callback or action is owed)
          </label>
          <div style={{ marginTop: 12 }}>
            <button style={btn} onClick={submit} disabled={saving}>
              {saving ? "Saving…" : "Save contact"}
            </button>
          </div>
        </div>
      )}

      {/* Open follow-ups */}
      {openFollowups.length > 0 && (
        <div style={{ ...card, borderColor: "#f6ad55" }}>
          <strong style={{ fontSize: 14 }}>Open follow-ups ({openFollowups.length})</strong>
          {openFollowups.map(c => (
            <div key={c.id} style={row}>
              <span style={{ minWidth: 0 }}>
                <strong>{c.company_name}</strong>
                <span style={meta}> · {c.contact_date} · {methodLabel(c.method)}</span>
                {c.subject && <span style={meta}> · {c.subject}</span>}
              </span>
              <button style={outline} onClick={() => resolveFollowup(c)}>Mark resolved</button>
            </div>
          ))}
        </div>
      )}

      {/* Log */}
      <div style={card}>
        <strong style={{ fontSize: 14 }}>Contacts — {year}{filterCompany && companies.length
          ? ` · ${companies.find(c => String(c.id) === filterCompany)?.name ?? ""}` : ""}</strong>
        {log.length === 0
          ? <p style={{ color: "#718096", fontSize: 13, margin: "6px 0 0" }}>No contacts recorded for {year}.</p>
          : log.map(c => (
            <div key={c.id} style={{ ...row, alignItems: "flex-start" }}>
              <span style={{ minWidth: 0 }}>
                <strong>{c.company_name}</strong>
                <span style={meta}> · {c.contact_date} · </span>
                <span style={{ fontWeight: 600, color: "#2d3748" }}>
                  {c.direction === "inbound" ? "Inbound" : "Outbound"} {methodLabel(c.method).toLowerCase()}
                </span>
                {c.follow_up_open && <span style={{ color: "#c05621" }}> · follow-up open</span>}
                {c.related_violation && (
                  <div style={{ fontSize: 12, marginTop: 2 }}>
                    <span style={{ background: "#fef5e7", color: "#c05621", border: "1px solid #f6ad55",
                      borderRadius: 4, padding: "1px 6px" }}>⚠ {c.related_violation}</span>
                  </div>
                )}
                {c.subject && <div style={{ fontSize: 13, marginTop: 2 }}>{c.subject}</div>}
                {c.summary && <div style={{ fontSize: 12, color: "#4a5568", marginTop: 2, whiteSpace: "pre-wrap" }}>{c.summary}</div>}
                <div style={{ ...meta, fontSize: 12, marginTop: 2 }}>
                  {[c.ca_staff_name && `CA: ${withTitle(c.ca_staff_name, c.ca_staff_title)}`,
                    c.iu_contact_name && `IU: ${withTitle(c.iu_contact_name, c.iu_contact_title)}`,
                    c.created_by_name && `logged by ${c.created_by_name}`]
                    .filter(Boolean).join(" · ")}
                </div>
              </span>
              <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {c.follow_up_open && <button style={outline} onClick={() => resolveFollowup(c)}>Resolve</button>}
                <button style={outline} onClick={() => onArchive(c)}>Archive</button>
              </span>
            </div>
          ))}
      </div>
    </div>
  );
};

const card: React.CSSProperties = { border: "1px solid #e2e8f0", borderRadius: 8, padding: 14, marginBottom: 14 };
const row: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center",
  gap: 8, fontSize: 13, padding: "7px 0", borderBottom: "1px solid #eee" };
const meta: React.CSSProperties = { color: "#718096" };
const field: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#4a5568" };
const inp: React.CSSProperties = { padding: "7px 9px", border: "1px solid #cbd5e0", borderRadius: 6, fontSize: 14 };
const sel: React.CSSProperties = { ...inp, padding: "6px 8px" };
const btn: React.CSSProperties = { background: "#3182ce", color: "#fff", border: "none", borderRadius: 6,
  padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const outline: React.CSSProperties = { background: "#fff", color: "#4a5568", border: "1px solid #cbd5e0",
  borderRadius: 6, padding: "3px 10px", fontSize: 12, cursor: "pointer" };

export default CommunicationsPanel;
